/**
 * Workflow Configuration Admin Page
 * Configure booking rules and notification settings
 * Feature 009: Workshop Booking & Job-Tracking System
 * T076
 */

import { useState, useEffect } from 'react';
import { Bell, Calendar, Save, Loader2 } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { pageStyles } from '../../lib/styles';

interface BookingRules {
  minAdvanceHours: number;
  maxAdvanceDays: number;
  slotDurationMinutes: number;
  bufferMinutes: number;
  allowSameDay: boolean;
  requireConfirmation: boolean;
  cancellationWindowHours: number;
}

interface NotificationConfig {
  emailEnabled: boolean;
  smsEnabled: boolean;
  sendReminder24h: boolean;
  sendReminder2h: boolean;
  sendStatusUpdates: boolean;
  sendCompletionNotice: boolean;
}

const DEFAULT_BOOKING_RULES: BookingRules = {
  minAdvanceHours: 24,
  maxAdvanceDays: 30,
  slotDurationMinutes: 60,
  bufferMinutes: 0,
  allowSameDay: false,
  requireConfirmation: true,
  cancellationWindowHours: 24,
};

const DEFAULT_NOTIFICATION_CONFIG: NotificationConfig = {
  emailEnabled: true,
  smsEnabled: false,
  sendReminder24h: true,
  sendReminder2h: true,
  sendStatusUpdates: true,
  sendCompletionNotice: true,
};

export function WorkflowConfigPage() {
  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [bookingRules, setBookingRules] = useState<BookingRules>(DEFAULT_BOOKING_RULES);
  const [notificationConfig, setNotificationConfig] = useState<NotificationConfig>(DEFAULT_NOTIFICATION_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch booking rules
  // Note: api.get returns the unwrapped `data` property from the API response
  const { data: bookingData, isLoading: loadingBooking } = useQuery({
    queryKey: ['admin', 'settings', 'bookingRules'],
    queryFn: () => api.get<{ key: string; value: BookingRules }>('/api/admin/settings/bookingRules'),
  });

  // Fetch notification config
  const { data: notificationData, isLoading: loadingNotification } = useQuery({
    queryKey: ['admin', 'settings', 'notificationConfig'],
    queryFn: () => api.get<{ key: string; value: NotificationConfig }>('/api/admin/settings/notificationConfig'),
  });

  const saveBookingMutation = useMutation({
    mutationFn: async (value: BookingRules) => {
      const result = await api.put('/api/admin/settings/bookingRules', { value });
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'bookingRules'] });
    },
  });

  const saveNotificationMutation = useMutation({
    mutationFn: (value: NotificationConfig) =>
      api.put('/api/admin/settings/notificationConfig', { value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'notificationConfig'] }),
  });

  useEffect(() => {
    if (bookingData?.value) {
      setBookingRules(bookingData.value);
    }
  }, [bookingData]);

  useEffect(() => {
    if (notificationData?.value) {
      setNotificationConfig(notificationData.value);
    }
  }, [notificationData]);

  const handleSave = async () => {
    try {
      await Promise.all([
        saveBookingMutation.mutateAsync(bookingRules),
        saveNotificationMutation.mutateAsync(notificationConfig),
      ]);
      setHasChanges(false);
      showSuccess('Configuración guardada correctamente');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  const isSaving = saveBookingMutation.isPending || saveNotificationMutation.isPending;
  const isLoading = loadingBooking || loadingNotification;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Flujo de Trabajo</h1>
          <p className={pageStyles.subtitle}>
            Reglas de reserva y notificaciones
          </p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={!hasChanges || isSaving}>
          {isSaving ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Save className="h-3 w-3 mr-1" />
          )}
          Guardar
        </Button>
      </div>

      {/* Booking Rules */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
          <Calendar className="h-3.5 w-3.5 text-blue-500" />
          <h2 className="text-xs font-medium text-gray-900 uppercase tracking-wide">Reglas de Reserva</h2>
        </div>
        <div className="p-3 space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Antelación mín. (h)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={bookingRules.minAdvanceHours}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, minAdvanceHours: parseInt(e.target.value) || 0 }));
                  setHasChanges(true);
                }}
                min={0}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Antelación máx. (días)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={bookingRules.maxAdvanceDays}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, maxAdvanceDays: parseInt(e.target.value) || 1 }));
                  setHasChanges(true);
                }}
                min={1}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Duración slot (min)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={bookingRules.slotDurationMinutes}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, slotDurationMinutes: parseInt(e.target.value) || 60 }));
                  setHasChanges(true);
                }}
                min={15}
                step={15}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Buffer (min)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={bookingRules.bufferMinutes}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, bufferMinutes: parseInt(e.target.value) || 0 }));
                  setHasChanges(true);
                }}
                min={0}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Cancelación (h)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={bookingRules.cancellationWindowHours}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, cancellationWindowHours: parseInt(e.target.value) || 0 }));
                  setHasChanges(true);
                }}
                min={0}
              />
            </div>
          </div>

          <div className="pt-2 space-y-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={bookingRules.allowSameDay}
                onChange={(e) => {
                  setBookingRules((prev) => ({ ...prev, allowSameDay: e.target.checked }));
                  setHasChanges(true);
                }}
                className="h-3.5 w-3.5"
              />
              Permitir reservas el mismo día
            </label>

            {/* Confirmation Mode Section */}
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] font-medium text-gray-500 uppercase mb-2">Modo de Confirmación</p>
              <div className="space-y-2">
                <label className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${!bookingRules.requireConfirmation ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="confirmationMode"
                    checked={!bookingRules.requireConfirmation}
                    onChange={() => {
                      setBookingRules((prev) => ({ ...prev, requireConfirmation: false }));
                      setHasChanges(true);
                    }}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs font-medium text-gray-900">Auto-confirmar</span>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Las reservas se confirman automáticamente sin verificación por email.
                    </p>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors ${bookingRules.requireConfirmation ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="confirmationMode"
                    checked={bookingRules.requireConfirmation}
                    onChange={() => {
                      setBookingRules((prev) => ({ ...prev, requireConfirmation: true }));
                      setHasChanges(true);
                    }}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs font-medium text-gray-900">Requiere verificación por email</span>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      El cliente debe hacer clic en un enlace enviado por email para confirmar.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Config */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
          <Bell className="h-3.5 w-3.5 text-blue-500" />
          <h2 className="text-xs font-medium text-gray-900 uppercase tracking-wide">Notificaciones</h2>
        </div>
        <div className="p-3 space-y-3">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={notificationConfig.emailEnabled}
                onChange={(e) => {
                  setNotificationConfig((prev) => ({ ...prev, emailEnabled: e.target.checked }));
                  setHasChanges(true);
                }}
                className="h-3.5 w-3.5"
              />
              Email
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={notificationConfig.smsEnabled}
                onChange={(e) => {
                  setNotificationConfig((prev) => ({ ...prev, smsEnabled: e.target.checked }));
                  setHasChanges(true);
                }}
                className="h-3.5 w-3.5"
              />
              SMS
            </label>
          </div>

          <div className="border-t border-gray-100 pt-2">
            <p className="text-[10px] font-medium text-gray-500 uppercase mb-2">Recordatorios</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={notificationConfig.sendReminder24h}
                  onChange={(e) => {
                    setNotificationConfig((prev) => ({ ...prev, sendReminder24h: e.target.checked }));
                    setHasChanges(true);
                  }}
                  className="h-3.5 w-3.5"
                />
                24h antes
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={notificationConfig.sendReminder2h}
                  onChange={(e) => {
                    setNotificationConfig((prev) => ({ ...prev, sendReminder2h: e.target.checked }));
                    setHasChanges(true);
                  }}
                  className="h-3.5 w-3.5"
                />
                2h antes
              </label>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-2">
            <p className="text-[10px] font-medium text-gray-500 uppercase mb-2">Estado</p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={notificationConfig.sendStatusUpdates}
                  onChange={(e) => {
                    setNotificationConfig((prev) => ({ ...prev, sendStatusUpdates: e.target.checked }));
                    setHasChanges(true);
                  }}
                  className="h-3.5 w-3.5"
                />
                Cambios de estado
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={notificationConfig.sendCompletionNotice}
                  onChange={(e) => {
                    setNotificationConfig((prev) => ({ ...prev, sendCompletionNotice: e.target.checked }));
                    setHasChanges(true);
                  }}
                  className="h-3.5 w-3.5"
                />
                Vehículo listo
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
